// ─── IMPORTANT ───────────────────────────────────────────────────────────────
// These helpers are called from within Immer-wrapped `set` callbacks in the store.
// The objects passed to them are Immer draft proxies, so direct mutations are
// safe and intentional.
//
// DO NOT use these functions outside of the store's `set` callbacks.
// Without an Immer draft, the mutations will corrupt the actual state.
// ─────────────────────────────────────────────────────────────────────────────

import {
    ChartDataSource,
    ChartSign,
    ConsolidatedMode,
    DataSourceState,
    FieldConfig,
    SeriesType,
    SeriesTypeValue,
} from '../types';
import { chartableDescriptorsMatcher, autogenerateHeading, isDescriptorRatio, getSeriesKey } from '../utils';
import { DescriptorProps } from '../../../constnats/descriptor';

export function findSource(dataSources: DataSourceState[], sourceId: number): DataSourceState | undefined {
    return dataSources.find((s) => s.id === sourceId);
}

export function cleanupHiddenSeries(
    hiddenSeries: Set<string>,
    existingSources: DataSourceState[],
    newIds: Set<number>,
): void {
    for (const existing of existingSources) {
        if (!newIds.has(existing.id)) {
            const keysToRemove = [...hiddenSeries].filter((k) => k.startsWith(`${existing.id}_`));
            for (const key of keysToRemove) {
                hiddenSeries.delete(key);
            }
        }
    }
}

function extractCommonFieldId(
    descriptor: Record<string, unknown>,
    consolidatedMode?: ConsolidatedMode,
): string | undefined {
    if (!consolidatedMode) {
        return undefined;
    }
    if (consolidatedMode === 'category') {
        const category = descriptor['category'] as { categoryId?: string | number } | null | undefined;
        return category?.categoryId != null ? String(category.categoryId) : undefined;
    }
    const relation = descriptor[DescriptorProps.STANDARDIZED_LABEL_RELATION] as { standardizedLabelId?: string } | null | undefined;
    return relation?.standardizedLabelId != null ? String(relation.standardizedLabelId) : undefined;
}

export function initFieldsCfg(
    descriptors: { id: string; label: string; type: string; viewScale?: number }[],
    prevCfg: Record<string, FieldConfig>,
    consolidatedMode?: ConsolidatedMode,
): Record<string, FieldConfig> {
    const chartable = descriptors.filter(chartableDescriptorsMatcher);
    const nextCfg: Record<string, FieldConfig> = {};

    for (const d of chartable) {
        const existing = prevCfg[d.id];
        const commonFieldId = extractCommonFieldId(d as Record<string, unknown>, consolidatedMode);
        nextCfg[d.id] = existing
            ? { ...existing, label: d.label, commonFieldId }
            : {
                  descriptorType: d.type,
                  annotationsEnabled: true,
                  ratio: isDescriptorRatio(d),
                  seriesType: SeriesType.LINE,
                  label: d.label,
                  chartSign: ChartSign.OFF,
                  commonFieldId,
                  separateAxis: false,
              };
    }

    return nextCfg;
}

export function getSelectedFieldIds(fieldsCfg: Record<string, FieldConfig>): string[] {
    return Object.entries(fieldsCfg)
        .filter(([, f]) => f.chartSign !== ChartSign.OFF)
        .map(([id]) => id);
}

export function reconcileDataSources(
    sources: ChartDataSource[],
    existingSources: DataSourceState[],
    consolidatedMode?: ConsolidatedMode,
): DataSourceState[] {
    const newDataSources: DataSourceState[] = [];

    for (const source of sources) {
        const existing = findSource(existingSources, source.id);

        if (existing) {
            existing.label = source.label;
            existing.companyId = source.companyId;
            existing.companyName = source.companyName;
            existing.companyLegendLabel = source.companyLegendLabel;
            existing.schemaId = source.schemaId;
            existing.schemaName = source.schemaName;
            existing.schemaLegendLabel = source.schemaLegendLabel;
            existing.reports = source.reports;

            const nextCfg = initFieldsCfg(source.descriptors, existing.fieldsCfg, consolidatedMode);
            existing.fieldsCfg = nextCfg;
            existing.selectedFieldIds = getSelectedFieldIds(nextCfg);
            newDataSources.push(existing);
        } else {
            const fieldsCfg = initFieldsCfg(source.descriptors, {}, consolidatedMode);

            const chartableDescs = source.descriptors.filter(chartableDescriptorsMatcher);
            if (
                chartableDescs.length > 0 &&
                !Object.values(fieldsCfg).some((f) => f.chartSign !== ChartSign.OFF)
            ) {
                fieldsCfg[chartableDescs[0].id] = {
                    ...fieldsCfg[chartableDescs[0].id],
                    chartSign: ChartSign.POSITIVE,
                };
            }

            newDataSources.push({
                id: source.id,
                label: source.label,
                companyId: source.companyId,
                companyName: source.companyName,
                companyLegendLabel: source.companyLegendLabel,
                schemaId: source.schemaId,
                schemaName: source.schemaName,
                schemaLegendLabel: source.schemaLegendLabel,
                reports: source.reports,
                fieldsCfg,
                selectedFieldIds: getSelectedFieldIds(fieldsCfg),
            });
        }
    }

    return newDataSources;
}

export function syncActiveTab(
    currentTab: number | null,
    newIds: Set<number>,
    newDataSources: DataSourceState[],
): number | null {
    if (currentTab === null || !newIds.has(currentTab)) {
        return newDataSources.length > 0 ? newDataSources[0].id : null;
    }
    return currentTab;
}

export function syncPeriods(
    currentPeriods: string[],
    dataSources: DataSourceState[],
    autoSelectAllPeriods = false,
): { availablePeriods: string[]; selectedPeriods: string[] } {
    const allPeriods = new Set<string>();
    for (const source of dataSources) {
        for (const report of source.reports) {
            allPeriods.add(report.period);
        }
    }
    const availablePeriods = Array.from(allPeriods);
    const filtered = currentPeriods.filter((p) => availablePeriods.includes(p));
    const selectedPeriods = !filtered.length && availablePeriods.length
        ? autoSelectAllPeriods ? availablePeriods : [availablePeriods[0]]
        : filtered;
    return { availablePeriods, selectedPeriods };
}

export function applyGlobalSeriesType(dataSources: DataSourceState[], seriesType: SeriesTypeValue): void {
    for (const source of dataSources) {
        for (const cfg of Object.values(source.fieldsCfg)) {
            cfg.seriesType = seriesType;
        }
    }
}

export function computeAutoHeadingFromSources(dataSources: DataSourceState[], consolidatedMode?: ConsolidatedMode): string {
    if (!consolidatedMode && dataSources.length > 1) {
        return '';
    }
    const allCfg: Record<string, FieldConfig> = {};
    for (const source of dataSources) {
        for (const [id, cfg] of Object.entries(source.fieldsCfg)) {
            if (cfg.chartSign !== ChartSign.OFF) {
                const key = consolidatedMode ? cfg.label : getSeriesKey(source.id, id);
                allCfg[key] = cfg;
            }
        }
    }
    return autogenerateHeading(allCfg);
}
